// src/pages/Settings.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import StorageService from '../utils/storage';
import MainLayout from '../components/MainLayout';
import apiService from '../services/api';

const Content = styled.div`
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 30px;
  color: white;
`;

const Form = styled.form`
  background: rgba(26, 16, 51, 0.5);
  backdrop-filter: blur(10px);
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 30px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(229, 9, 20, 0.5);
    box-shadow: 0 0 0 2px rgba(229, 9, 20, 0.2);
  }
  
  option {
    background: #1a1033;
    color: white;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  
  input {
    width: 20px;
    height: 20px;
    margin: 0;
    cursor: pointer;
    
    &:checked {
      accent-color: #E50914;
    }
  }
  
  label {
    margin: 0;
    cursor: pointer;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #E50914;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #F40D17;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: rgba(229, 9, 20, 0.5);
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
  padding: 16px;
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const RefreshButton = styled(Button)`
  background-color: #2196F3;
  
  &:hover {
    background-color: #0d8bf2;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const InfoMessage = styled.div`
  margin-top: 20px;
  padding: 16px;
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.2);
  color: #2196F3;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Settings = () => {
  const [settings, setSettings] = useState({
    playerQuality: 'auto',
    autoplay: true,
    darkMode: false
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');

  useEffect(() => {
    const loadSettings = () => {
      const savedSettings = StorageService.getSettings();
      setSettings(savedSettings);
    };

    loadSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
    
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    StorageService.updateSettings(settings);
    setSaveSuccess(true);
    
    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);
  };

  const handleRefreshChannels = async () => {
    try {
      setRefreshing(true);
      setRefreshMessage('Atualizando lista de canais...');
      
      // Forçar atualização da lista de canais
      await apiService.refreshChannelList();
      
      setRefreshMessage('Lista de canais atualizada com sucesso!');
      setTimeout(() => {
        setRefreshMessage('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao atualizar lista de canais:', error);
      setRefreshMessage('Erro ao atualizar lista de canais. Tente novamente.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <MainLayout>
      <Content>
        <PageTitle>Configurações</PageTitle>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="playerQuality">Qualidade do Vídeo</Label>
            <Select
              id="playerQuality"
              name="playerQuality"
              value={settings.playerQuality}
              onChange={handleChange}
            >
              <option value="auto">Automática</option>
              <option value="hd">Alta Definição (HD)</option>
              <option value="sd">Definição Padrão (SD)</option>
              <option value="low">Baixa</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                id="autoplay"
                name="autoplay"
                checked={settings.autoplay}
                onChange={handleChange}
              />
              <Label htmlFor="autoplay">
                Iniciar reprodução automaticamente
              </Label>
            </Checkbox>
          </FormGroup>
          
          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                id="darkMode"
                name="darkMode"
                checked={settings.darkMode}
                onChange={handleChange}
              />
              <Label htmlFor="darkMode">
                Modo Escuro
              </Label>
            </Checkbox>
          </FormGroup>
          
          <Button type="submit">
            Salvar Configurações
          </Button>
          
          {saveSuccess && (
            <SuccessMessage>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Configurações salvas com sucesso!
            </SuccessMessage>
          )}
        </Form>
        
        <FormGroup style={{ marginTop: '30px' }}>
          <RefreshButton 
            type="button" 
            onClick={handleRefreshChannels}
            disabled={refreshing}
          >
            {refreshing && <LoadingSpinner />}
            {refreshing ? 'Atualizando...' : 'Atualizar Lista de Canais'}
          </RefreshButton>
          
          {refreshMessage && (
            <InfoMessage>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 6.66667V10" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 13.3333H10.0083" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {refreshMessage}
            </InfoMessage>
          )}
        </FormGroup>
      </Content>
    </MainLayout>
  );
};

export default Settings;